import { LatLng } from "../../maps/map-provider.interface";
import { GeoAddress } from "../address-search-provider.interface";

/**
 * Places API (New) returns components as longText/shortText; the Maps JS geocoder still uses
 * long_name/short_name. Both are normalized to this shape before mapping.
 */
export type GoogleAddressComponent = {
    types: string[],
    longText?: string,
    shortText?: string
};

export function toGoogleAddressComponents(
    components: google.maps.GeocoderAddressComponent[] | null | undefined
): GoogleAddressComponent[] {
    return (components ?? []).map(component => ({
        types: component.types,
        longText: component.long_name,
        shortText: component.short_name
    }));
}

export function toGeoAddress(
    coords: LatLng,
    formattedAddress: string | null | undefined,
    components: GoogleAddressComponent[]
): GeoAddress {
    const getLongText = (type: string): string | undefined =>
        components.find(c => c.types.includes(type))?.longText || undefined;

    const getShortText = (type: string): string | undefined =>
        components.find(c => c.types.includes(type))?.shortText || undefined;

    const street = getLongText("route");

    return {
        coords,
        label: formattedAddress ?? undefined,

        countryCode: getShortText("country"),
        countryName: getLongText("country"),

        stateCode: getShortText("administrative_area_level_1"),
        stateName: getLongText("administrative_area_level_1"),

        // City fallbacks across locales
        city: getLongText("locality")
            ?? getLongText("postal_town")
            ?? getLongText("administrative_area_level_3")
            ?? getLongText("administrative_area_level_2"),

        postalCode: getLongText("postal_code"),

        street,
        streetShortName: getShortText("route") ?? street,

        houseNumber: getLongText("street_number"),

        district: getLongText("neighborhood")
            ?? getLongText("sublocality_level_1")
            ?? getLongText("sublocality"),

        sublocality: getLongText("sublocality_level_1") ?? getLongText("sublocality")
    };
}
